import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  TablePagination,
  Avatar,
  IconButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IoEyeOutline } from "react-icons/io5";
import { MdEdit } from "react-icons/md";
import { themeOrange, themeBlue } from "../../../utils/colorTheme";

export const UserTable = ({ searchQuery = "" }) => {
  const navigate = useNavigate();

  const { get_users_list_by_role_data } = useSelector(
    (state) => state.dashboard_users
  );

  const [rows, setRows] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (get_users_list_by_role_data?.length) {
      const sorted = [...get_users_list_by_role_data].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setAllUsers(sorted);
    }
  }, [get_users_list_by_role_data]);

  useEffect(() => {
    let filtered = allUsers;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          `${u?.first_name} ${u?.last_name}`.toLowerCase().includes(q) ||
          u?.email?.toLowerCase().includes(q) ||
          u?.phone?.includes(q)
      );
    }
    setRows(filtered);
    setPage(0);
  }, [searchQuery, allUsers]);

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {["User", "Email", "Phone", "Joined", "Actions"].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#999",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    borderBottom: "1px solid #f0f0f0",
                    py: 1.5,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: "#bbb", fontSize: "14px" }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, i) => (
                  <TableRow
                    key={i}
                    hover
                    sx={{
                      "&:hover": { bgcolor: "#FAFBFC" },
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onClick={() => navigate(`/users/user-profile/${row?.id}`)}
                  >
                    <TableCell sx={{ borderBottom: "1px solid #f5f5f5", py: 1.8 }}>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Avatar
                          src={row?.avatar || ""}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: "#E3F2FD",
                            color: "#1565C0",
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        >
                          {row?.first_name?.[0]}{row?.last_name?.[0]}
                        </Avatar>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: themeBlue,
                            textTransform: "capitalize",
                          }}
                        >
                          {`${row?.first_name || ""} ${row?.last_name || ""}`}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                      <Typography sx={{ fontSize: "13px", color: "#555" }}>
                        {row?.email || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                      <Typography sx={{ fontSize: "13px", color: "#555" }}>
                        {row?.phone || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                      <Typography sx={{ fontSize: "12px", color: "#999" }}>
                        {row?.created_at
                          ? new Date(row.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                      <Stack onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/users/user-profile/${row?.id}`)}
                            sx={{
                              color: themeOrange,
                              bgcolor: "#FFF0EB",
                              borderRadius: "8px",
                              "&:hover": { bgcolor: "#FFE0D5" },
                            }}
                          >
                            <IoEyeOutline size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/users/edit-user/${row?.id}`)}
                            sx={{
                              color: themeBlue,
                              bgcolor: "#E8EAF6",
                              borderRadius: "8px",
                              "&:hover": { bgcolor: "#D1D5EE" },
                            }}
                          >
                            <MdEdit size={16} />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: "1px solid #f0f0f0",
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: "12px",
            color: "#999",
          },
        }}
      />
    </Box>
  );
};
